import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

async function req(path: string, init: RequestInit = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" }, ...init,
  });
  if (!res.ok) { let d = res.statusText; try { d = (await res.json()).detail || d; } catch { /**/ } throw new Error(String(d)); }
  return res.json();
}

const STATUS_AR: Record<string, string> = {
  draft: "مسودة", testing: "تحت التجربة", approved: "معتمدة", rejected: "مرفوضة", production: "إنتاج",
};
const NEXT: Record<string, string[]> = {
  draft: ["testing"], testing: ["approved", "rejected"], approved: ["production"], rejected: ["testing"], production: [],
};

function Label({ code, name, conc, date }: { code: string; name: string; conc?: string | null; date?: string }) {
  return (
    <div className="bl-print-label">
      <QRCodeSVG value={`${ORIGIN}/pureh/blend/scan/${code}`} size={72} />
      <div>
        <div style={{ fontWeight: 800, fontSize: 13 }}>{code}</div>
        <div style={{ fontSize: 11 }}>{name}</div>
        <div style={{ fontSize: 10 }}>{conc || ""} · {date ? date.slice(0, 10) : ""}</div>
      </div>
    </div>
  );
}

export default function BlendDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sampleMl, setSampleMl] = useState(10);
  const [resultFor, setResultFor] = useState<string | null>(null);
  const [resHours, setResHours] = useState(6);
  const [resNote, setResNote] = useState("");
  const printRef = useRef<"labels" | "sheet" | null>(null);

  const load = useCallback(() => {
    req(`/blends/${id}/detail`).then(setData).catch((e) => setErr(String(e.message || e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="detail-error">{err}</div>;
  if (!data) return <div className="bl-sub py-8 text-center" data-testid="detail-loading">جارٍ التحميل…</div>;

  const b = data.blend;
  const qc: { item: string; done: boolean }[] = b.qc_checklist || [];

  const setStatus = async (s: string) => {
    try { await req(`/blends/${b.id}/status`, { method: "PATCH", body: JSON.stringify({ status: s }) }); toast.success(`الحالة: ${STATUS_AR[s]}`); load(); }
    catch (e: any) { toast.error(String(e.message || e)); }
  };
  const toggleQc = async (i: number, done: boolean) => {
    try { await req(`/blends/${b.id}/qc`, { method: "PATCH", body: JSON.stringify({ index: i, done }) }); load(); }
    catch (e: any) { toast.error(String(e.message || e)); }
  };
  const addSample = async () => {
    try { await req(`/blends/${b.id}/samples`, { method: "POST", body: JSON.stringify({ volume_ml: sampleMl }) }); toast.success("عينة جديدة + QR"); load(); }
    catch (e: any) { toast.error(String(e.message || e)); }
  };
  const addResult = async (sid: string) => {
    try {
      await req(`/samples/${sid}/results`, { method: "POST", body: JSON.stringify({ afterHours: resHours, note: resNote }) });
      toast.success("اتسجّلت النتيجة");
      setResultFor(null); setResNote(""); load();
    } catch (e: any) { toast.error(String(e.message || e)); }
  };
  const promote = async () => {
    if (!window.confirm(`ترقية «${b.name}» لـ Product Master؟ هيتعمل منتج رسمي + تركيبة معتمدة FK.`)) return;
    try {
      const res: any = await req(`/blends/${b.id}/promote`, { method: "POST" });
      toast.success(`اترقّى ✓ ${res.productNumber} — دلوقتي في الكتالوج والطلبات`);
      load();
    } catch (e: any) { toast.error(String(e.message || e)); }
  };
  const doPrint = (kind: "labels" | "sheet") => {
    printRef.current = kind;
    document.body.setAttribute("data-print", kind);
    window.print();
    setTimeout(() => document.body.removeAttribute("data-print"), 500);
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="blend-detail-page">
      <div className="bl-card">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bl-title bl-en" data-testid="detail-code">{b.code || b.id}</span>
          <span className="font-extrabold">{b.name}</span>
          <span className="bl-pill" data-testid="detail-status">{STATUS_AR[b.status || "draft"]}</span>
          {b.promoted && <span className="bl-pill" style={{ color: "var(--bl-ok)" }} data-testid="detail-promoted-pill">في Product Master ✓</span>}
          <button className="bl-btn ghost mr-auto" style={{ padding: "6px 14px" }}
            onClick={() => doPrint("labels")} data-testid="detail-print-labels">🖨 اطبع Labels</button>
        </div>
        <div className="bl-sub bl-en mt-1">
          {b.body?.oil} {b.body?.pct}% + {b.direction?.oil} {b.direction?.pct}% · {(b.boosters || []).length} boosters · {b.concentration}
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          {(NEXT[b.status || "draft"] || []).map((s) => (
            <button key={s} className="bl-btn primary" style={{ padding: "6px 16px" }}
              onClick={() => setStatus(s)} data-testid={`detail-status-${s}`}>→ {STATUS_AR[s]}</button>
          ))}
          {!b.promoted && (
            <button className="bl-btn primary" style={{ padding: "6px 16px" }} onClick={promote}
              data-testid="detail-promote-btn">⬆ رقّي لـ Product Master</button>
          )}
          <button className="bl-btn ghost" style={{ padding: "6px 16px" }} data-testid="detail-open-calc"
            onClick={() => {
              sessionStorage.setItem("blend_handoff", JSON.stringify({
                name: b.name, base_target_id: b.base_target_id, blend_id: b.id,
                body: b.body, direction: b.direction, boosters: b.boosters,
                concentration: b.concentration, batch: b.batch || null, warnings: b.warnings || [],
              }));
              nav("/pureh/blend/calculator");
            }}>افتح في الحاسبة</button>
        </div>
      </div>

      <div className="bl-card" data-testid="qc-checklist">
        <div className="bl-eyebrow mb-2">✅ QC Checklist — سجل الجودة</div>
        {qc.map((q, i) => (
          <label key={i} className="flex items-center gap-2 py-1 cursor-pointer text-sm" data-testid={`qc-item-${i}`}>
            <input type="checkbox" checked={q.done} onChange={(e) => toggleQc(i, e.target.checked)}
              style={{ accentColor: "var(--bl-gold)" }} data-testid={`qc-check-${i}`} />
            <span style={{ textDecoration: q.done ? "line-through" : "none", color: q.done ? "var(--bl-ink-muted)" : undefined }}>{q.item}</span>
          </label>
        ))}
        {!qc.length && <div className="bl-sub">مفيش بنود — القائمة بتتولّد مع الحفظ من التحذيرات</div>}
      </div>

      <div className="bl-card" data-testid="samples-section">
        <div className="bl-eyebrow mb-2">🧪 العينات (Samples)</div>
        <div className="flex gap-2 items-end mb-3">
          <div><label className="bl-label">حجم العينة (مل)</label>
            <input type="number" className="bl-input" style={{ width: 90 }} value={sampleMl}
              onChange={(e) => setSampleMl(Number(e.target.value) || 0)} data-testid="sample-ml-input" /></div>
          <button className="bl-btn primary" onClick={addSample} data-testid="add-sample-btn">+ عينة جديدة (QR)</button>
        </div>
        {data.samples.map((s: any) => (
          <div key={s.id} className="py-2" style={{ borderTop: "1px solid var(--bl-line)" }} data-testid={`sample-row-${s.code}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <QRCodeSVG value={`${ORIGIN}/pureh/blend/scan/${s.code}`} size={40} />
              <span className="font-bold bl-en">{s.code}</span>
              <span className="bl-sub">{s.volume_ml} مل · {s.concentration}</span>
              <button className="bl-btn ghost mr-auto" style={{ padding: "4px 12px" }}
                onClick={() => setResultFor(resultFor === s.id ? null : s.id)} data-testid={`sample-add-result-${s.code}`}>سجّل نتيجة</button>
            </div>
            {(s.test_results || []).map((r: any, i: number) => (
              <div key={i} className="bl-sub mt-1" data-testid={`sample-result-${s.code}-${i}`}>
                ⏱ بعد {r.afterHours} ساعة: {r.note}
              </div>
            ))}
            {resultFor === s.id && (
              <div className="flex gap-2 items-end mt-2 flex-wrap">
                <div><label className="bl-label">بعد كام ساعة</label>
                  <input type="number" className="bl-input" style={{ width: 80 }} value={resHours}
                    onChange={(e) => setResHours(Number(e.target.value) || 0)} data-testid="result-hours-input" /></div>
                <div style={{ flex: 1, minWidth: 160 }}><label className="bl-label">الملاحظة</label>
                  <input className="bl-input" value={resNote} onChange={(e) => setResNote(e.target.value)}
                    placeholder="لسه محسوس · اللون ثابت…" data-testid="result-note-input" /></div>
                <button className="bl-btn primary" onClick={() => addResult(s.id)} data-testid="result-save-btn">حفظ</button>
              </div>
            )}
          </div>
        ))}
        {!data.samples.length && <div className="bl-sub">مفيش عينات لسه</div>}
      </div>

      <div className="bl-card" data-testid="batches-section">
        <div className="bl-eyebrow mb-2">🏭 التشغيلات (Batches)</div>
        {data.batches.map((x: any) => (
          <div key={x.id} className="py-2" style={{ borderTop: "1px solid var(--bl-line)" }} data-testid={`batch-row-${x.code}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <QRCodeSVG value={`${ORIGIN}/pureh/blend/scan/${x.code}`} size={40} />
              <span className="font-bold bl-en">{x.code}</span>
              <span className="bl-sub">{x.bottles} × {x.sizeMl} مل</span>
              {x.deducted_from_inventory && <span className="bl-pill">اتخصم من المخزون ✓</span>}
              <button className="bl-btn ghost mr-auto" style={{ padding: "4px 12px" }}
                onClick={() => doPrint("sheet")} data-testid={`batch-sheet-${x.code}`}>🖨 بطاقة معمل</button>
            </div>
            {(x.unmatched || []).length > 0 && (
              <div className="bl-sub mt-1" style={{ color: "var(--bl-danger)" }}>غير موجود في المكتبة: {x.unmatched.join("، ")}</div>
            )}
          </div>
        ))}
        {!data.batches.length && <div className="bl-sub">أكّد تشغيلة من الحاسبة وهتظهر هنا</div>}
      </div>

      {/* Print-only: labels sheet + batch sheet */}
      <div className="bl-print-only bl-print-labels-sheet">
        <Label code={b.code} name={b.name} conc={b.concentration} date={b.created_at} />
        {data.samples.map((s: any) => <Label key={s.id} code={s.code} name={b.name} conc={s.concentration} date={s.created_at} />)}
        {data.batches.map((x: any) => <Label key={x.id} code={x.code} name={b.name} conc={b.concentration} date={x.created_at} />)}
      </div>
      <div className="bl-print-only bl-print-sheet">
        <h2>ORVAN · بطاقة معمل — {b.name}</h2>
        <p>الكود: {b.code} · التاريخ: {new Date().toISOString().slice(0, 10)} · الحالة: {STATUS_AR[b.status || "draft"]}</p>
        <table><thead><tr><th>المكوّن</th><th>%</th></tr></thead>
          <tbody>
            <tr><td>{b.body?.oil}</td><td>{b.body?.pct}%</td></tr>
            <tr><td>{b.direction?.oil}</td><td>{b.direction?.pct}%</td></tr>
            {(b.boosters || []).map((bo: any, i: number) => <tr key={i}><td>{bo.material}</td><td>{bo.pct}%</td></tr>)}
            <tr><td>التركيز</td><td>{b.concentration}</td></tr>
          </tbody></table>
        <h3>خطوات التصنيع</h3>
        <ol>
          <li>خلط الزيوت والمُعزّزات (وزن بالجرام)</li>
          <li>إضافة الكحول + BHT</li>
          <li>Maceration 4–6 أسابيع (بارد ومظلم)</li>
          <li>Chill-filter ‑15°م / 0.45µm</li>
          <li>QC: لون · رائحة · ثبات</li>
        </ol>
        <h3>تحذيرات</h3>
        <ul>{(b.warnings || []).map((w: string, i: number) => <li key={i}>{w}</li>)}</ul>
        <p>رقم التشغيلة: ______________ · توقيع/QC: ______________</p>
        <p style={{ fontStyle: "italic" }}>* أداة إدارة — التقييم النهائي بالشمّ واختبار فعلي.</p>
      </div>
    </div>
  );
}
